import React from 'react';
import styled, { css } from 'styled-components';
import { Fonts, Palette } from '../../components/utils';
import { Slider } from 'antd';
import { Select } from '../../components/ui/Select';
import { FormField, FormLabel, FormMessage } from '../../components/ui/Form';
import { Badge } from '../../components/ui/Badge';
import { StyledModal } from '../../components/Modals';
import { SettingsModalInput } from '../settings/SettingsModals';
import { Flex } from '../../components/ui/Flex';
import { TemperatureInput } from './TemperatureInput';

export const StyledLabel = styled(FormLabel)`
  color: ${Palette.BLACK};
  font-family: ${Fonts.OPEN_SANS_FONT}; 
  font-size: 0.875rem;
  font-style: normal;
  font-weight: 600;
  line-height: 1.5rem;
  letter-spacing: 0rem;
`;

export const StyledSelect = styled(Select)`
  height: 3rem; 
  color: ${Palette.CONTENT_TERTIARY};
  font-family: ${Fonts.OPEN_SANS_FONT};
  font-size: 0.875rem;
  font-style: normal;
  font-weight: 400;
  line-height: 1.5rem; 
  letter-spacing: 0rem;

  .ant-select-selection-placeholder {
    color: ${Palette.CONTENT_TERTIARY} !important;
    font-size: 0.875rem !important;
    font-style: normal;
    font-weight: 400 !important;
    line-height: 1.5rem !important; 
    letter-spacing: 0;
  }

  .ant-select-selection-item {
    color: ${Palette.CONTENT_TERTIARY} !important;
    font-size: 0.875rem !important;
    font-style: normal;
    font-weight: 400 !important;
    line-height: 1.5rem !important; 
    letter-spacing: 0;
  }
`;

export const StyledSlider = styled(Slider)`
  width: 100%;
  margin: 0;

  &.ant-slider .ant-slider-rail,
  &.ant-slider:hover .ant-slider-rail,
  &.ant-slider .ant-slider-rail:hover,
  & .ant-slider .ant-slider-rail,
  & .ant-slider:hover .ant-slider-rail,
  & .ant-slider .ant-slider-rail:hover {
    background-color: #13315559 !important;
    height: 3px;
  }

  &.ant-slider .ant-slider-track,
  &.ant-slider:hover .ant-slider-track,
  &.ant-slider .ant-slider-track:hover,
  & .ant-slider .ant-slider-track,
  & .ant-slider:hover .ant-slider-track,
  & .ant-slider .ant-slider-track:hover {
    background-color: ${Palette.HAZY_BLUE} !important;
    height: 3px;
  }
  
  .ant-slider:hover .ant-slider-rail {
    background-color: #13315559 !important;
    height: 3px;
  }

  .ant-slider:hover .ant-slider-track {
    background-color: ${Palette.HAZY_BLUE} !important;
    height: 3px;
  }

  .ant-slider {
    :hover {
      background-color: #13315559;
    }
  }

  .ant-slider-rail { 
    background-color: #13315559;
    border-radius: 0;
    height: 3px;

    :hover {
      background-color: #13315559;
    }
  }

  .ant-slider-handle {
    margin-top: -8px;
    width: 20px;
    height: 20px;
    border: none;
    background-color: ${Palette.HAZY_BLUE};

    &:hover {
      background-color: ${Palette.HAZY_BLUE};
    }
  }

  .ant-slider-track {
    background-color: ${Palette.HAZY_BLUE};
    height: 3px;

    &:hover {
      background-color: ${Palette.HAZY_BLUE};
    }
  }
`;

export const StyledBadge = styled(Badge)`
  cursor: pointer;
  gap: 8px;
  background-color: ${Palette.WHITE};
  color: ${Palette.BLACK};
  position: relative;
  z-index: 0;
  min-width: 5rem;
  max-width: 6rem;
  height: 1.5rem;
  width: 100%;

  ${({ variant }) => {
    switch (variant) {
      case 'orange':
        return css`
          border: 1px solid ${Palette.ORANGE_500};
          :hover {
            background-color: ${Palette.ORANGE_100};
          }
        `;
      case 'blue':
        return css`
          border: 1px solid ${Palette.OCEANIC};
          :hover {
            background-color: ${Palette.OCEANIC_100};
          }
        `;
      case 'lightGrey':
        return css`
          border: 1px solid ${Palette.BLACK_50};
          color: ${Palette.BLACK_50};
          :hover {
            background-color: ${Palette.DISABLED};
          }
        `;
      case 'off':
        return css`
          border: 1px solid ${Palette.BLACK_50};
          background-color: ${Palette.DISABLED};

          p {
            color: ${Palette.BLACK_50};
          }

          svg {
            color: ${Palette.BLACK_50} !important;
          }
          :hover {
            cursor: not-allowed;
            background-color: ${Palette.DISABLED};
          }
        `;
      case 'gradient':
        return css`
          border: none;
          position: relative;
          z-index: 0;
          transition: 0.3s ease;

          &:before {
            content: "";
            position: absolute;
            z-index: -2;
            inset: 0;
            padding: 1px;
            border-radius: inherit;
            background: linear-gradient(90deg, ${Palette.ORANGE_500}, ${Palette.OCEANIC});
            mask: linear-gradient(#fff 0 0) content-box, 
                  linear-gradient(#fff 0 0);
            mask-composite: exclude;
            -webkit-mask: linear-gradient(#fff 0 0) content-box, 
                          linear-gradient(#fff 0 0);
            -webkit-mask-composite: xor;
          }

          &:after {
            content: "";
            position: absolute;
            z-index: -1;
            inset: 1px;
            border-radius: inherit;
            background: linear-gradient(90deg, ${Palette.ORANGE_500}, ${Palette.OCEANIC});
            opacity: 0;
            transition: opacity 0.3s ease;
          }

          &:hover {
            &:after {
              opacity: 0.1;
            }
          }
        `;
      default:
        return '';
    }
  }}
`;

export const TitleText = styled.p`
  color: ${Palette.BLACK};
  font-family: ${Fonts.RED_HAT_FONT};
  font-size: 1.25rem;
  font-style: normal;
  font-weight: 800;
  line-height:  2rem;
  letter-spacing: 0rem;
  margin: 0;
`;

export const SubTitleText = styled.p`
  color: ${Palette.BLACK};
  font-family: ${Fonts.OPEN_SANS_FONT};
  font-size: 1rem;
  font-style: normal;
  font-weight: 600;
  line-height: 1.5rem;
  letter-spacing: 0rem;
  margin: 0;
`;

export const Text = styled.p`
  color: ${Palette.CONTENT_TERTIARY};
  font-family: ${Fonts.OPEN_SANS_FONT};
  font-size: 0.75rem;
  font-style: normal;
  font-weight: 400;
  line-height: 1.25rem;
  letter-spacing: 0rem;
  margin: 0;

  ${({ bold }) =>
    bold &&
    `
    color: ${Palette.BLACK};
    font-weight: 600;
  `}
`;

export const ThermostatModal = styled(StyledModal)`
  height: 100%;

  .modal-content {
    gap: 0;
    height: 100%;
    padding: 0 !important;
    box-shadow: 0px 1px 18px 0px rgba(0, 0, 0, 0.12);
    overflow: scroll;
  }

  .modal-dialog .modal-content .modal-header .modal-title {
    font-size: 25px;
    line-height: 40px;
  }
`;

const StyledTemperatureRangeControlBox = styled(Flex)`
  min-width: 40px;
  height: 40px;
  border: 1px solid ${Palette.LIGHT_GREY};
  border-radius: 0.3125rem;
  user-select: none;
  font-family: ${Fonts.OPEN_SANS_FONT};
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.5rem;
  color: ${Palette.BLACK};
  position: relative;
  cursor: pointer;
  
  &:hover {
    border-color: ${Palette.OCEANIC};
  }
  
  &:focus-within {
    border-color: ${Palette.OCEANIC};
    outline: none;
  }
`;

const TransparentInput = styled.input`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  color: transparent;
  caret-color: ${Palette.BLACK};
  padding: 0;
  font-family: ${Fonts.OPEN_SANS_FONT};
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.5rem;
  border: none;
  background-color: transparent;
  outline: none;
  text-align: center;
  z-index: 2;
`;

const ValueDisplay = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 1;
`;

export const TemperatureRangeControl = ({ name, label, minMax = [45, 80], control }) => {
  return (
    <Flex direction="column" gap="0.5rem">
      <Text bold>{label}</Text>
      <FormField
        control={control}
        name={name}
        render={({ field }) => {
          const handleMinChange = (e) => {
            const newValue = parseInt(e.target.value);
            if (!isNaN(newValue) && newValue >= minMax[0] && newValue <= minMax[1] && newValue <= field.value[1]) {
              field.onChange([newValue, field.value[1]]);
            }
          };

          const handleMaxChange = (e) => {
            const newValue = parseInt(e.target.value);
            if (!isNaN(newValue) && newValue >= minMax[0] && newValue <= minMax[1] && newValue >= field.value[0]) {
              field.onChange([field.value[0], newValue]);
            }
          };

          return (
            <Flex alignItems="center" justify="center">
              <StyledTemperatureRangeControlBox justify="center" alignItems="center">
                <TransparentInput
                  type="number"
                  value={field.value[0]}
                  onChange={handleMinChange}
                  onBlur={(e) => {
                    const newValue = parseInt(e.target.value);
                    if (isNaN(newValue) || newValue < minMax[0] || newValue > minMax[1] || newValue > field.value[1]) {
                      e.target.value = field.value[0];
                    }
                  }}
                  min={minMax[0]}
                  max={field.value[1]}
                />
                <ValueDisplay>{field.value[0]}</ValueDisplay>
              </StyledTemperatureRangeControlBox>
              <StyledSlider range {...field} min={minMax[0]} max={minMax[1]} />
              <StyledTemperatureRangeControlBox justify="center" alignItems="center">
                <TransparentInput
                  type="number"
                  value={field.value[1]}
                  onChange={handleMaxChange}
                  onBlur={(e) => {
                    const newValue = parseInt(e.target.value);
                    if (isNaN(newValue) || newValue < minMax[0] || newValue > minMax[1] || newValue < field.value[0]) {
                      e.target.value = field.value[1];
                    }
                  }}
                  min={field.value[0]}
                  max={minMax[1]}
                />
                <ValueDisplay>{field.value[1]}</ValueDisplay>
              </StyledTemperatureRangeControlBox>
            </Flex>
          );
        }}
      />
    </Flex>
  );
};

export const SnapBackTemperatureControl = ({ name, control , buildingTempUnit = 'F' }) => {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field: { onChange, value } }) => (
        <Flex direction="column" gap="4px">
          <Flex gap="2.5rem" justify="space-between">
            <Flex direction="column" gap="4px" style={{ width: "60%" }}>
              <SubTitleText style={{ fontSize: '0.75rem' }}>
                Snap Back Setpoint
              </SubTitleText>
              <Text>
                Set a temperature that these units will automatically return to.
              </Text>
            </Flex>

            <TemperatureInput
              cssOverride={"width: 5.75rem;"}
              defaultValue={value}
              value={value}
              alwaysShow
              unitSuffix={`°${buildingTempUnit}`}
              unitDisplay={`°${buildingTempUnit}`}
              onSubmit={(inputValue) => {
                onChange(inputValue)
              }} />
          </Flex>
          <FormMessage />
        </Flex>
      )}
    />
  );
};

export const SetPointRefreshFrequencyControl = ({ name, control }) => {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field: { onChange, value } }) => (
        <Flex direction="column" gap="4px">
          <Flex gap="2.5rem" justify="space-between">
            <Flex direction="column" gap="4px" style={{ width: "60%" }}>
              <SubTitleText style={{ fontSize: '0.75rem' }}>
                Setpoint Refresh Frequency
              </SubTitleText>
              <Text>
                How long after a temperature override before the thermostat reverts to its snap back setpoint.
              </Text>
            </Flex>
            <TemperatureInput
              cssOverride={"width: 5.75rem;"}
              defaultValue={value}
              alwaysShow
              value={value}
              unitSuffix={'hrs'}
              unitDisplay={'hrs'}
              onSubmit={(inputValue) => {
                onChange(inputValue)
              }} />
          </Flex>
          <FormMessage />
        </Flex>
      )}
    />
  );
};
